import { injectable } from 'inversify';
import { autorun, makeAutoObservable, reaction } from 'mobx';
import { queryClient } from '@shared/api/reactQuery';
import { NavigateFunction } from 'react-router-dom';
import { PATH_CABINET } from '@app/routes/paths';
import { ProfileModule } from '@mobx/services/profile';
import { wizardSteps, wizardStepsChangeUnionType } from '@widgets/registrationWizard/config/steps';
import { isEqual } from 'lodash';
import { getGetApiUsersIdRegistrationsQueryKey } from '@/shared/api/orval/users/users';
import { DbRegistration } from '@/shared/api/orval/models';
import {
  getGetApiRegistrationsIdDocsQueryOptions,
  getGetApiRegistrationsIdQueryKey,
  getGetApiRegistrationsIdQueryOptions,
  getGetApiRegistrationsReqdocsQueryKey,
  getGetApiRegistrationsReqdocsQueryOptions,
  getPutApiRegistrationsIdWizardStepMutationOptions
} from '@/shared/api/orval/registrations/registrations';

@injectable()
export class RegistrationWizardViewModel {
  location: any = null;

  navigate: NavigateFunction | null = null;

  registration: DbRegistration | null = null;

  wizardStep: number | undefined = undefined;

  isPrevStepAllowed = true;

  isLoading = false;

  isFinishStep = false;

  isFinishAllowed = false;

  isNextStepAllowed = false;

  wizardSteps = wizardSteps;

  /**
   * Флаг смены формы управления (CHANGE_UNION_TYPE).
   * Подгружается из registration.registrationKind после ввода адреса/БИН на шаге info.
   * При true wizard подменяет стандартный шаг «Счета» на ChangeUnionTypeAccountsStep.
   */
  get isChangeUnionType(): boolean {
    return this.registration?.registrationKind === 'CHANGE_UNION_TYPE';
  }

  /**
   * OSI-808: значение registrations.wizard_step в БД может оказаться вне диапазона
   * реально существующих шагов (0..wizardSteps.length-1) — например "4" при 4 шагах
   * (info/docs/accounts/sign, валидные индексы 0..3). Причина — снятый ниже баг off-by-one
   * в nextStep, из-за которого с последнего шага можно было "уйти" на несуществующий.
   * MUI Stepper с activeStep больше индекса последнего Step считает ВСЕ шаги completed
   * и ни один active — StepContent (Collapse in={active}) не монтируется НИ У ОДНОГО шага,
   * экран становится пустым (так залипли заявки 2887, 1327 на бою).
   * Кламп только для отображения: сырое this.wizardStep НЕ переписываем, чтобы не задеть
   * prevStep/nextStep и не мутировать боевые данные неявно из UI — при заходе на такую
   * заявку она откроется на последнем существующем шаге вместо пустого экрана.
   */
  get displayWizardStep(): number {
    const maxIndex = this.wizardSteps.length - 1;
    if (maxIndex < 0) return 0;

    return Math.min(Math.max(this.wizardStep ?? 0, 0), maxIndex);
  }

  private _mutationCache = queryClient.getMutationCache();

  private _queryCache = queryClient.getQueryCache();

  private _disposeAllReactionsController = new AbortController();

  constructor(private _profileModule: ProfileModule) {
    makeAutoObservable(this);

    reaction(
      () => this.location,
      (location) => {
        if (!location?.state?.registrationId) {
          this.registration = null;
        }
      },
      { signal: this._disposeAllReactionsController.signal }
    );

    autorun(
      async () => {
        if (!this.location) return;

        try {
          this.isLoading = true;
          await this.loadRegistrationData();
        } finally {
          this.isLoading = false;
        }
      },
      { signal: this._disposeAllReactionsController.signal }
    );

    autorun(
      async () => {
        try {
          this.isLoading = true;
          await this.defineWizardSteps();
        } finally {
          this.isLoading = false;
        }
      },
      { signal: this._disposeAllReactionsController.signal }
    );

    autorun(
      () => {
        this.wizardStep = Number(this.registration?.wizardStep ?? 0);
      },
      { signal: this._disposeAllReactionsController.signal }
    );

    this._queryCache.subscribe(({ query }) => {
      const getRegistrationKey = getGetApiRegistrationsIdQueryKey(this.registration?.id ?? 0);
      if (query.queryKey[0] === getRegistrationKey[0]) {
        if (query.state.fetchStatus === 'fetching') this.isLoading = true;

        if (!query.state.data?.result) return;

        if (isEqual(this.registration, query.state.data.result)) return;

        this.registration = query.state.data.result;
        this.isLoading = false;
      }
    });
  }

  cleanup = () => {
    this._disposeAllReactionsController.abort();
  };

  prevStep = async () => {
    if (!this.wizardStep) return;

    const wizardStepMutation = this._mutationCache.build(
      queryClient,
      getPutApiRegistrationsIdWizardStepMutationOptions()
    );

    try {
      this.isLoading = true;

      const result = await wizardStepMutation.execute({
        id: this.registration?.id ?? 0,
        data: (this.wizardStep - 1).toString()
      });
      if (result.code !== 0) return;

      await queryClient.refetchQueries({ queryKey: getGetApiRegistrationsIdQueryKey(this.registration?.id ?? 0) });
      await queryClient.invalidateQueries({
        queryKey: getGetApiUsersIdRegistrationsQueryKey(this._profileModule.userData.id)
      });
    } catch (e) {
      this.isLoading = false;
    }
  };

  nextStep = async () => {
    // OSI-808: было `>= this.wizardSteps.length` — off-by-one. На последнем валидном шаге
    // (индекс wizardSteps.length - 1) это условие ложно, переход выполнялся и в БД
    // сохранялось wizard_step = wizardSteps.length — несуществующий шаг (см. displayWizardStep
    // выше про последствия для Stepper). Сравниваем с последним ВАЛИДНЫМ индексом.
    if ((this.wizardStep ?? 0) >= this.wizardSteps.length - 1) return;

    const wizardStepMutation = this._mutationCache.build(
      queryClient,
      getPutApiRegistrationsIdWizardStepMutationOptions()
    );
    try {
      this.isLoading = true;
      const result = await wizardStepMutation.execute({
        id: this.registration?.id ?? 0,
        data: ((this.wizardStep ?? 0) + 1).toString()
      });
      if (result.code !== 0) return;

      await queryClient.refetchQueries({ queryKey: getGetApiRegistrationsIdQueryKey(this.registration?.id ?? 0) });
      await queryClient.invalidateQueries({
        queryKey: getGetApiUsersIdRegistrationsQueryKey(this._profileModule.userData.id)
      });
    } catch (e) {
      this.isLoading = false;
    }
  };

  finish = async () => {
    const wizardStepMutation = this._mutationCache.build(
      queryClient,
      getPutApiRegistrationsIdWizardStepMutationOptions()
    );

    try {
      this.isLoading = true;
      const result = await wizardStepMutation.execute({
        id: this.registration?.id ?? 0,
        data: 'finish'
      });
      if (result.code !== 0) return;

      this.navigate?.(PATH_CABINET.root);
      await queryClient.refetchQueries({
        queryKey: getGetApiUsersIdRegistrationsQueryKey(this._profileModule.userData.id)
      });
    } catch (e) {
      this.isLoading = false;
    }
  };

  exit = () => {
    this.navigate?.(PATH_CABINET.root);
  };

  private defineWizardSteps = async () => {
    if (!this.registration?.id) return;

    const registrationId = this.registration?.id;

    // Ветка CHANGE_UNION_TYPE: шаг счетов заменяется на ChangeUnionTypeAccountsStep.
    // Стандартная логика CURRENT_IBAN_INFO/SAVING_IBAN_INFO не применяется.
    if (this.isChangeUnionType) {
      this.wizardSteps = wizardStepsChangeUnionType;
      return;
    }

    const requiredDocsResponse = await queryClient.fetchQuery(
      getGetApiRegistrationsReqdocsQueryOptions(
        { registrationId },
        {
          query: {
            queryKey: [...getGetApiRegistrationsReqdocsQueryKey({ registrationId }), `${this.registration.unionTypeId}`]
          }
        }
      )
    );

    const isRequiredCurrentIbanInfo = requiredDocsResponse?.result?.some(
      (reqDoc) => reqDoc.code === 'CURRENT_IBAN_INFO' && reqDoc.isRequired
    );

    const registrationDocs =
      (await queryClient.fetchQuery(getGetApiRegistrationsIdDocsQueryOptions(registrationId)))?.result ?? [];

    const isAccountDocsApplied = registrationDocs.some(
      (doc) => doc.docTypeCode === 'CURRENT_IBAN_INFO' || doc.docTypeCode === 'SAVING_IBAN_INFO'
    );

    const isNeedAccountsStep = isRequiredCurrentIbanInfo || isAccountDocsApplied;

    if (!isNeedAccountsStep) {
      this.wizardSteps = wizardSteps.filter((step) => step.code !== 'accounts');
    } else {
      this.wizardSteps = wizardSteps;
    }
  };

  private loadRegistrationData = async () => {
    const selectedRegistrationId = this.location?.state?.registrationId;
    if (!selectedRegistrationId) return;

    const data = await queryClient.fetchQuery(getGetApiRegistrationsIdQueryOptions(selectedRegistrationId));
    if (!data.result) return;

    this.registration = data.result;
  };
}

export const token = Symbol.for('IRegistrationWizardViewModel');
